import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertTriangle, Loader2, Paperclip, Send, X } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { explainError } from '@/lib/explain-error'
import { useToast } from '@/hooks/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import type { Order } from '@/types'

/**
 * Send a customer the licence email for an order.
 *
 * This replaces opening one of 26 Google Docs, deleting the three account-manager
 * paragraphs that do not apply, filling in the customer name and licence term by hand,
 * and pasting the result into Gmail.
 *
 * The CRM fills in what it knows and says what it could not - it does not quietly leave a
 * field blank. Anything it could not resolve is listed above the body AND left visible as
 * {{a_placeholder}} inside it, because an empty space reads like finished text.
 */

interface OrderEmailDialogProps {
  order: Order
  open: boolean
  onOpenChange: (open: boolean) => void
}

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024

/** Merge-field names as a person would say them. */
const FIELD_LABELS: Record<string, string> = {
  contact_first_name: "the customer's first name",
  contact_full_name: "the customer's full name",
  contact_email: "the customer's email address",
  account_name: 'the account name',
  order_number: 'the order number',
  order_total: 'the order total',
  term_start: 'the licence start date',
  term_end: 'the licence end date',
  product_list: 'the products on this order',
  product_name: 'the product name',
  user_list: 'the named users',
  account_manager_name: "the account manager's name",
  account_manager_email: "the account manager's email address",
  sender_first_name: 'your first name',
  // The CRM does not hold licence keys, so this one is ALWAYS reported. That is
  // deliberate: sending a MadeToTag renewal without the key is the failure this catches.
  license_code: 'the licence key (the CRM cannot know this, paste it in)',
}

export function OrderEmailDialog({ order, open, onOpenChange }: OrderEmailDialogProps) {
  const { toast } = useToast()
  const fileRef = useRef<HTMLInputElement>(null)

  const [templateId, setTemplateId] = useState('')
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState('')
  const [html, setHtml] = useState('')
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState('')
  const [unresolved, setUnresolved] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [category, setCategory] = useState<string | null>(null)
  const [billingEmail, setBillingEmail] = useState<string | null>(null)
  // While the feature is being tested, order emails are restricted to internal addresses.
  // The server enforces it; this is so nobody writes an email they cannot send.
  const [restriction, setRestriction] = useState<string | null>(null)
  const [allowedDomains, setAllowedDomains] = useState<string[] | null>(null)

  // Which template the content on screen actually belongs to.
  //
  // Choosing a template used to fire a preview and leave the previous subject and body in
  // place, with Send still enabled - so picking B and sending immediately sent A. Two
  // previews in flight could also land out of order, overwriting the newer with the older.
  // Nothing is sendable unless loadedFor matches the current selection.
  const loadedFor = useRef<string>('')
  const inFlight = useRef<string>('')

  const templates = useQuery({
    queryKey: ['email-templates', order.id],
    queryFn: () => apiClient.getEmailTemplates(order.id),
    enabled: open,
  })

  // Default the recipient to the billing contact. Order creation requires that contact to
  // have an email, so for any order raised in the CRM this is always populated.
  useEffect(() => {
    if (open) setTo(order.bill_to_contact_email || '')
  }, [open, order.bill_to_contact_email])

  const preview = useMutation({
    mutationFn: (id: string) => {
      inFlight.current = id
      return apiClient.previewOrderEmail(order.id, id).then((data) => ({ id, data }))
    },
    onSuccess: ({ id, data }) => {
      // A late response for a template the user has moved on from must not overwrite the
      // one they are looking at.
      if (id !== inFlight.current) return
      setSubject(data.subject)
      setHtml(data.html_body)
      setText(data.text_body)
      setUnresolved(data.unresolved)
      setWarnings(data.warnings)
      setCategory(data.template_category)
      setBillingEmail(data.billing_contact_email)
      setRestriction(data.recipient_restriction)
      setAllowedDomains(data.allowed_domains)
      // An internal template goes to a colleague, so the customer's address is cleared
      // rather than left in the To field waiting to be sent by accident.
      setTo(data.to_address ?? '')
      loadedFor.current = id
      setError('')
    },
    onError: (err) => setError(explainError(err, 'Could not build that email.')),
  })

  /** Start again from a chosen template, discarding whatever was on screen. */
  function chooseTemplate(id: string) {
    setTemplateId(id)
    // Cleared, not left behind: a half-replaced draft belonging to another template is the
    // one thing that must never be sendable.
    setSubject('')
    setHtml('')
    setText('')
    setUnresolved([])
    setWarnings([])
    setCategory(null)
    loadedFor.current = ''
    preview.mutate(id)
  }

  const send = useMutation({
    mutationFn: () =>
      apiClient.sendOrderEmail(order.id, {
        template_id: templateId,
        to_address: to,
        cc_address: cc || undefined,
        subject,
        html_body: html,
        text_body: text,
        attachments: files,
      }),
    onSuccess: (result) => {
      toast({
        title: 'Email sent',
        description:
          result.warnings && result.warnings.length > 0
            ? result.warnings.join(' ')
            : `Sent to ${to}.`,
      })
      onOpenChange(false)
      reset()
    },
    onError: (err) => setError(explainError(err, 'The email could not be sent.')),
  })

  function reset() {
    setTemplateId('')
    setSubject('')
    setHtml('')
    setText('')
    setFiles([])
    setUnresolved([])
    setWarnings([])
    setCategory(null)
    setBillingEmail(null)
    loadedFor.current = ''
    inFlight.current = ''
    setError('')
  }

  const totalBytes = useMemo(() => files.reduce((n, f) => n + f.size, 0), [files])
  const overSize = totalBytes > MAX_ATTACHMENT_BYTES

  const chosenTemplate = useMemo(
    () => templates.data?.flatMap((g) => g.templates).find((t) => t.id === templateId),
    [templates.data, templateId]
  )

  const recipients = useMemo(
    () =>
      to
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean),
    [to]
  )

  /**
   * Recipients the testing restriction will refuse.
   *
   * Mirrors the server so Send is unavailable rather than failing at the last moment. The
   * server is the one that counts.
   */
  const blockedByRestriction = useMemo(() => {
    if (!allowedDomains) return []
    return recipients.filter((a) => {
      const at = a.toLowerCase().lastIndexOf('@')
      return at === -1 || !allowedDomains.includes(a.toLowerCase().slice(at + 1))
    })
  }, [recipients, allowedDomains])

  /** An internal template may only go to colleagues, matching the server's rule. */
  const internalToOutsider =
    category === 'internal' && recipients.some((a) => !a.toLowerCase().endsWith('@grackledocs.com'))

  /** Said before the send, not reported after Gmail has already accepted it. */
  const notBillingContact =
    !!billingEmail &&
    category !== 'internal' &&
    recipients.length > 0 &&
    !recipients.some((a) => a.toLowerCase() === billingEmail.toLowerCase())

  const canSend =
    !!to.trim() &&
    !!subject.trim() &&
    !!html.trim() &&
    !overSize &&
    !send.isPending &&
    // The content on screen must belong to the template that is selected. Covers both a
    // preview still in flight and a draft cleared by a change of template.
    loadedFor.current === templateId &&
    !preview.isPending &&
    // The dialog asks for unresolved fields to be replaced; before this it did not
    // actually stop the send, so they went to the customer as written.
    unresolved.length === 0 &&
    !internalToOutsider &&
    blockedByRestriction.length === 0

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Email the customer</DialogTitle>
          <DialogDescription>
            Order {order.order_number}
            {order.account_name ? ` for ${order.account_name}` : ''}. Sent from your own Gmail, and
            recorded against this order.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="order-email-template">Template</Label>
            <Select
              value={templateId}
              onValueChange={chooseTemplate}
              disabled={templates.isLoading || send.isPending}
            >
              <SelectTrigger id="order-email-template" aria-describedby="template-help">
                <SelectValue
                  placeholder={templates.isLoading ? 'Loading templates' : 'Choose a template'}
                />
              </SelectTrigger>
              <SelectContent>
                {templates.data?.map((group) => (
                  <SelectGroup key={group.category}>
                    <SelectLabel>{group.label}</SelectLabel>
                    {group.templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {/* "Suggested" is stated in words, not by colour or position
                            alone, so it survives for anyone not seeing the styling. */}
                        {t.name}
                        {t.suggested ? ' (suggested for this order)' : ''}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <p id="template-help" className="text-xs text-muted-foreground mt-1">
              Templates matching this order&apos;s products are listed first. Everything below is
              editable before it goes.
            </p>
          </div>

          {preview.isPending && (
            <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
              <Loader2
                className="mr-2 inline h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              Building the email from {chosenTemplate?.name ?? 'the template'}.
            </p>
          )}

          {/* Warnings and unresolved fields sit in one live region so a screen reader
              hears them once, on arrival, rather than as several interruptions. */}
          <div aria-live="polite">
            {warnings.map((w) => (
              <Alert key={w} role="alert" className="mb-2">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>Worth a look before sending</AlertTitle>
                <AlertDescription>{w}</AlertDescription>
              </Alert>
            ))}

            {restriction && (
              <Alert role="note" className="mb-2">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>Testing mode: internal addresses only</AlertTitle>
                <AlertDescription>{restriction}</AlertDescription>
              </Alert>
            )}

            {blockedByRestriction.length > 0 && (
              <Alert variant="destructive" role="alert" className="mb-2">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>{blockedByRestriction[0]} cannot be emailed yet</AlertTitle>
                <AlertDescription>
                  While the feature is being tested this can only send to{' '}
                  {(allowedDomains ?? []).map((d) => `@${d}`).join(' or ')}. Use an internal address
                  to try it out.
                </AlertDescription>
              </Alert>
            )}

            {internalToOutsider && (
              <Alert variant="destructive" role="alert" className="mb-2">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>This template is not for customers</AlertTitle>
                <AlertDescription>
                  It contains order details meant for colleagues. Send it to a grackledocs.com
                  address, or choose a customer template instead.
                </AlertDescription>
              </Alert>
            )}

            {/* Said here, before the send. The server also returns this after the fact,
                which is no use to anyone deciding whether to press the button. */}
            {notBillingContact && (
              <Alert role="alert" className="mb-2">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>Not this order&apos;s billing contact</AlertTitle>
                <AlertDescription>
                  This is going to {recipients.join(', ')}, and the billing contact on the order is{' '}
                  {billingEmail}. That is often deliberate, but a typo looks the same.
                </AlertDescription>
              </Alert>
            )}

            {unresolved.length > 0 && (
              <Alert variant="destructive" role="alert" className="mb-2">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {unresolved.length} thing{unresolved.length === 1 ? '' : 's'} the CRM could not
                  fill in
                </AlertTitle>
                <AlertDescription>
                  <ul className="mt-1 space-y-0.5">
                    {unresolved.map((f) => (
                      <li key={f}>{FIELD_LABELS[f] ?? f}</li>
                    ))}
                  </ul>
                  <p className="mt-2">
                    Each one is still showing in the message below as{' '}
                    <span className="font-mono text-xs">{`{{${unresolved[0]}}}`}</span>. Replace
                    them before sending, or the customer will receive them as they are.
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="order-email-to">To</Label>
              <Input
                id="order-email-to"
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={send.isPending}
                aria-describedby="to-help"
              />
              <p id="to-help" className="text-xs text-muted-foreground mt-1">
                Defaults to the billing contact on this order.
              </p>
            </div>
            <div>
              <Label htmlFor="order-email-cc">Cc (optional)</Label>
              <Input
                id="order-email-cc"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="Separate several with commas"
                disabled={send.isPending}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="order-email-subject">Subject</Label>
            <Input
              id="order-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={send.isPending}
            />
          </div>

          <div>
            <Label htmlFor="order-email-body">Message</Label>
            <RichTextEditor
              id="order-email-body"
              label="Message"
              value={html}
              onChange={(nextHtml, nextText) => {
                setHtml(nextHtml)
                setText(nextText)
              }}
              disabled={send.isPending}
            />
          </div>

          <div>
            <Label htmlFor="order-email-files">Attachments</Label>
            <Input
              id="order-email-files"
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.gif"
              disabled={send.isPending}
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
              aria-describedby="files-help"
            />
            <p id="files-help" className="text-xs text-muted-foreground mt-1">
              Several templates promise a user guide. Up to 5 files, 10 MB in total.
            </p>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f) => (
                  <li key={f.name} className="flex items-center gap-2 text-sm">
                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                    <span className="truncate">{f.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {(f.size / 1024).toFixed(0)} kB
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => setFiles(files.filter((x) => x !== f))}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {overSize && (
              <Alert variant="destructive" role="alert" className="mt-2">
                <AlertTitle>Those attachments are too big</AlertTitle>
                <AlertDescription>
                  {(totalBytes / 1024 / 1024).toFixed(1)} MB in total, and the limit is 10 MB.
                  Remove one, or send a link instead.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {error && (
            <Alert variant="destructive" role="alert">
              <AlertTitle>The email was not sent</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={send.isPending}
          >
            Cancel
          </Button>
          {/* A disabled button with no explanation is its own bug. */}
          {!canSend && !send.isPending && (
            <p className="text-sm text-muted-foreground mr-auto" role="status">
              {preview.isPending || loadedFor.current !== templateId
                ? 'Waiting for the template to load.'
                : unresolved.length > 0
                  ? 'Replace the unfilled fields first.'
                  : blockedByRestriction.length > 0
                    ? 'Testing mode: internal addresses only.'
                    : internalToOutsider
                      ? 'Internal template: send it to a grackledocs.com address.'
                      : overSize
                        ? 'The attachments are over the limit.'
                        : 'Fill in the recipient, subject and message.'}
            </p>
          )}
          <Button type="button" onClick={() => send.mutate()} disabled={!canSend}>
            {send.isPending ? (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            ) : (
              <Send className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {send.isPending ? 'Sending' : 'Send email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
